import React, { useEffect, useRef } from "react";

export const HIDDEN_INPUT_STYLE: React.CSSProperties = {
  position: "absolute", opacity: 0, height: 0, overflow: "hidden", zIndex: -1, width: 0, pointerEvents: "auto",
};

/**
 * Joins truthy class name fragments into a single space-separated string.
 *
 * @param classes Class fragments; falsy values are dropped.
 * @returns The combined class string.
 */
export function cn(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Creates the set of refs used by the Select component (container, desktop and
 * mobile inputs, and a flag tracking portal interaction).
 *
 * @returns An object holding the four stable refs.
 */
export function useSelectRefs() {
  return {
    containerRef: useRef<HTMLDivElement>(null),
    inputRef: useRef<HTMLInputElement>(null),
    mobileInputRef: useRef<HTMLInputElement>(null),
    portalInteractingRef: useRef(false),
  };
}

/**
 * Determines whether a mousedown target lies outside the container and its
 * portal, meaning the dropdown should close.
 *
 * @param target The event target node.
 * @param containerRef Ref to the select container element.
 * @returns `true` when the click was truly outside.
 */
function isOutsideClick(
  target: Node,
  containerRef: React.RefObject<HTMLElement | null>,
): boolean {
  if (!containerRef.current || containerRef.current.contains(target)) return false;
  const portalEl = (target as HTMLElement).closest?.('[data-select-portal]');
  return !portalEl;
}

/**
 * Closes the dropdown when the user clicks outside the container (and outside
 * any select portal) while it is active.
 *
 * @param active Whether the outside-click listener should be attached.
 * @param containerRef Ref to the select container element.
 * @param onClose Callback invoked to close the dropdown.
 */
export function useClickOutsideClose(
  active: boolean,
  containerRef: React.RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  useEffect(() => {
    if (!active) return;
    const handler = (event: MouseEvent) => {
      if (isOutsideClick(event.target as Node, containerRef)) setTimeout(() => onClose(), 0);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [active, onClose, containerRef]);
}

/**
 * Focuses the mobile input once its overlay becomes visible, deferring across
 * two animation frames so the element is mounted and laid out first.
 *
 * @param show Whether the mobile overlay is shown.
 * @param ref Ref to the mobile input element.
 */
export function useMobileAutoFocus(show: boolean, ref: React.RefObject<HTMLInputElement | null>) {
  useEffect(() => {
    if (show && ref.current) requestAnimationFrame(() => requestAnimationFrame(() => ref.current?.focus()));
  }, [show, ref]);
}

/**
 * Locks body scroll while the mobile select overlay is open and restores the
 * original body styles when it closes.
 *
 * @param show Whether the mobile overlay is shown.
 */
export function useMobileOverlayBodyLock(show: boolean) {
  useEffect(() => {
    if (!show) return;
    const orig = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = orig.overflow;
      document.body.style.position = orig.position;
      document.body.style.width = orig.width;
    };
  }, [show]);
}
